import { HealthResponseSchema, type HealthResponse } from '@ste/shared';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message?: string,
    /** The whole error body, e.g. the existing game of a 409 game_exists. */
    public body?: Record<string, unknown>,
  ) {
    super(message ?? code);
  }
}

let csrfToken: string | null = null;
export const setCsrfToken = (token: string | null) => {
  csrfToken = token;
};

/** JSON request against the API; mutations carry the session's CSRF token. */
export async function api<T>(method: string, url: string, body?: unknown, fetchImpl: typeof fetch = fetch): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetchImpl(url, {
    method,
    headers,
    credentials: 'same-origin',
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (res.status === 204) return undefined as T;
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) throw new ApiError(res.status, String(data.error ?? 'error'), data.message as string | undefined, data);
  return data as T;
}

/** File contents as base64 for the JSON upload routes (PDFs up to 5 MB). */
export async function fileToBase64(file: Blob): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(binary);
}

export async function fetchHealth(fetchImpl: typeof fetch = fetch): Promise<HealthResponse> {
  const res = await fetchImpl('/api/health');
  return HealthResponseSchema.parse(await res.json());
}

export interface Me {
  stage: 'totp' | 'enroll' | 'active';
  csrfToken: string;
  user: { id: string; email: string; name: string; role: 'admin' | 'viewer' } | null;
}

export async function fetchMe(): Promise<Me | null> {
  try {
    const me = await api<Me>('GET', '/api/auth/me');
    setCsrfToken(me.csrfToken);
    return me;
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw e;
  }
}
